import { useState, useRef, useCallback, useEffect } from 'react';
import { useChatStore } from '../../store/chatStore';
import { api } from '../../services/api';
import type { RipgrepResponse, RipgrepFileMatch } from '../../types';
import { X, Search, FileText, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Loader2, HelpCircle } from 'lucide-react';

export function RipgrepPanel() {
  const { toggleRipgrepPanel } = useChatStore();
  const [query, setQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [category, setCategory] = useState('');
  const [results, setResults] = useState<RipgrepResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedFiles, setCollapsedFiles] = useState<Set<string>>(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Abort in-flight request on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    // Abort previous request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setError(null);
    setCollapsedFiles(new Set());

    try {
      const response = await api.ripgrep(trimmed, {
        caseSensitive,
        category: category || undefined,
      }, controller.signal);
      setResults(response);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [query, caseSensitive, category]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleFileCollapse = (file: string) => {
    setCollapsedFiles(prev => {
      const next = new Set(prev);
      if (next.has(file)) {
        next.delete(file);
      } else {
        next.add(file);
      }
      return next;
    });
  };

  // Highlight matching text in a line
  const highlightMatch = (text: string, searchTerm: string, isCaseSensitive: boolean) => {
    if (!searchTerm) return text;

    // Use 'g' for split (to capture groups) but NOT for test — regex.test()
    // with 'g' flag has stateful lastIndex that causes alternating true/false.
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const splitRegex = new RegExp(`(${escaped})`, isCaseSensitive ? 'g' : 'gi');
    const testRegex = new RegExp(`^${escaped}$`, isCaseSensitive ? '' : 'i');
    const parts = text.split(splitRegex);

    return parts.map((part, i) => {
      if (testRegex.test(part)) {
        return (
          <span key={i} className="bg-yellow-300 text-gray-900 font-semibold rounded-sm px-0.5">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Extract short filename from path
  const shortFileName = (path: string) => {
    const parts = path.split('/');
    return parts.length > 1 ? parts.slice(-2).join('/') : path;
  };

  return (
    <div className="h-full flex flex-col bg-gray-950 text-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Search size={16} className="text-green-400" />
          Ripgrep Search
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`p-1 rounded transition-colors ${showHelp ? 'bg-gray-700 text-green-400' : 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'}`}
            aria-label="Search help"
            title="Search tips"
          >
            <HelpCircle size={16} />
          </button>
          <button
            onClick={toggleRipgrepPanel}
            className="p-1 rounded hover:bg-gray-800 transition-colors"
            aria-label="Close ripgrep panel"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Help Tips */}
      {showHelp && (
        <div className="px-4 py-3 border-b border-gray-800 bg-gray-900/60 text-xs text-gray-300 space-y-2">
          <p className="font-semibold text-green-400 text-sm">Quick Search Tips</p>
          <ul className="space-y-1.5 list-none">
            <li><kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-green-300 font-mono text-[11px]">Enter</kbd> to search after typing your query</li>
            <li><span className="text-yellow-400 font-semibold">Case Sensitive</span> — toggle on to match exact casing (e.g. <code className="bg-gray-800 px-1 rounded">"Error"</code> won't match <code className="bg-gray-800 px-1 rounded">"error"</code>)</li>
            <li><span className="text-yellow-400 font-semibold">Exact phrases</span> — type the full phrase to match (e.g. <code className="bg-gray-800 px-1 rounded">connection timeout</code>)</li>
            <li><span className="text-yellow-400 font-semibold">Category filter</span> — narrow results to a specific doc category</li>
            <li><span className="text-yellow-400 font-semibold">File groups</span> — click a file header to collapse/expand its matches</li>
            <li><span className="text-yellow-400 font-semibold">Line numbers</span> — shown in <span className="text-green-500">green</span> next to each match</li>
          </ul>
        </div>
      )}

      {/* Search Controls */}
      <div className="px-3 py-3 border-b border-gray-800 space-y-2">
        {/* Search Input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search text..."
            className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 font-mono"
            autoFocus
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !query.trim()}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded text-sm transition-colors flex items-center gap-1"
          >
            {isSearching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          </button>
        </div>

        {/* Options row */}
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <button
            onClick={() => setCaseSensitive(!caseSensitive)}
            className="flex items-center gap-1 hover:text-gray-200 transition-colors"
          >
            {caseSensitive ? (
              <ToggleRight size={16} className="text-green-400" />
            ) : (
              <ToggleLeft size={16} />
            )}
            Case sensitive
          </button>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-green-500"
          >
            <option value="">All categories</option>
            <option value="SVM_Contracts">SVM Contracts</option>
            <option value="Trouble-Shooting-Guide">Troubleshooting</option>
            <option value="User_Manuals">User Manuals</option>
          </select>
        </div>
      </div>

      {/* Results Area */}
      <div className="flex-1 overflow-y-auto font-mono text-xs">
        {error && (
          <div className="px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {results && !error && (
          <>
            {/* Summary */}
            <div className="px-4 py-2 text-gray-500 border-b border-gray-800 sticky top-0 bg-gray-950 z-10">
              {results.total_matches} match{results.total_matches !== 1 ? 'es' : ''} in{' '}
              {results.files_matched} file{results.files_matched !== 1 ? 's' : ''}
            </div>

            {results.results.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Search size={32} className="mx-auto mb-3 opacity-40" />
                <p>No matches found for "{results.query}"</p>
                <p className="mt-1 text-xs">Try a different search term or category</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800/50">
                {results.results.map((fileMatch: RipgrepFileMatch) => (
                  <FileMatchGroup
                    key={fileMatch.file}
                    fileMatch={fileMatch}
                    searchTerm={results.query}
                    caseSensitive={caseSensitive}
                    isCollapsed={collapsedFiles.has(fileMatch.file)}
                    onToggle={() => toggleFileCollapse(fileMatch.file)}
                    highlightMatch={highlightMatch}
                    shortFileName={shortFileName}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {!results && !error && !isSearching && (
          <div className="px-4 py-8 text-center text-gray-500">
            <Search size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">Search for text in source documents</p>
            <p className="mt-1 text-xs">Results show file name, line number, and matching content</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FileMatchGroup({
  fileMatch,
  searchTerm,
  caseSensitive,
  isCollapsed,
  onToggle,
  highlightMatch,
  shortFileName,
}: {
  fileMatch: RipgrepFileMatch;
  searchTerm: string;
  caseSensitive: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  highlightMatch: (text: string, term: string, cs: boolean) => React.ReactNode;
  shortFileName: (path: string) => string;
}) {
  return (
    <div>
      {/* File header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 hover:bg-gray-900 transition-colors text-left group"
      >
        {isCollapsed ? (
          <ChevronRight size={12} className="text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown size={12} className="text-gray-500 flex-shrink-0" />
        )}
        <FileText size={12} className="text-purple-400 flex-shrink-0" />
        <span className="text-purple-400 font-semibold truncate" title={fileMatch.file}>
          {shortFileName(fileMatch.file)}
        </span>
        <span className="text-gray-600 ml-auto flex-shrink-0">
          {fileMatch.lines.length} match{fileMatch.lines.length !== 1 ? 'es' : ''}
        </span>
      </button>

      {/* Matching lines */}
      {!isCollapsed && (
        <div className="pb-1">
          {fileMatch.lines.map((line, idx) => (
            <div
              key={idx}
              className="flex px-3 py-0.5 hover:bg-gray-900/50 transition-colors"
            >
              <span className="text-green-500 w-8 text-right mr-3 flex-shrink-0 select-none">
                {line.line_number}
              </span>
              <span className="text-gray-300 break-all whitespace-pre-wrap leading-relaxed">
                {highlightMatch(line.text, searchTerm, caseSensitive)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
